import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Check } from "lucide-react";
import PhotoUploader from "@/components/PhotoUploader";

const STEPS = ["Location", "Space", "Capacity", "Vehicles", "Features", "Keys", "Photos", "Description", "Pricing", "Review"];

const SPACE_TYPES = [
  ["driveway", "Driveway"], ["garage", "Garage"], ["parking_bay", "Parking bay"],
  ["gated", "Gated parking"], ["commercial", "Commercial"], ["hotel", "Hotel"], ["office", "Office"], ["land", "Land"],
];
const VEHICLE_SIZES = ["Motorcycle", "Small car", "Standard car", "Large car", "SUV", "Van"];
const FEATURE_OPTIONS = ["CCTV", "Lighting", "Gated", "Covered", "EV charging", "24hr access", "Disabled access", "Security", "Airport transfer"];
const KEY_OPTIONS = [["customer_keeps", "Customer keeps keys"], ["host_keeps", "Host keeps keys"], ["either", "Either"]];

const DEFAULT_IMAGES = [];

export default function HostListingWizard() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [airports, setAirports] = useState([]);
  const [form, setForm] = useState({
    address_line: "", city: "", postcode: "", country: "United Kingdom",
    airport_code: "LHR", space_type: "driveway", capacity: 1,
    vehicle_sizes: ["Standard car"], max_length_m: null, max_width_m: null, max_height_m: null,
    features: ["CCTV"], key_handling: "customer_keeps",
    images: DEFAULT_IMAGES, title: "", description: "", parking_instructions: "",
    base_daily_price: 6.5, distance_miles: 2.5, transfer_minutes: 12,
    terms_accepted: false,
  });
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/airports").then(r => setAirports(r.data)); }, []);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggle = (k, v) => setForm(f => ({ ...f, [k]: f[k].includes(v) ? f[k].filter(x => x !== v) : [...f[k], v] }));

  const next = () => setStep(s => Math.min(STEPS.length - 1, s + 1));
  const prev = () => setStep(s => Math.max(0, s - 1));

  const submit = async () => {
    setErr("");
    if (form.images.length < 3) return setErr("Please upload at least 3 photos of the parking space.");
    if (!form.terms_accepted) return setErr("You must accept the Host Terms");
    setSubmitting(true);
    try {
      await api.post("/host/listings", form);
      nav("/host");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 lg:px-8 py-10">
      <div className="flex items-center gap-2 mb-2">
        {STEPS.map((_, i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-[var(--fpn-amber)]" : "bg-slate-200"}`} data-testid={`wizard-progress-${i}`}/>
        ))}
      </div>
      <div className="text-xs font-bold tracking-[0.18em] uppercase text-slate-500">Step {step + 1} of {STEPS.length}</div>
      <h1 className="font-display text-3xl font-semibold text-[var(--fpn-navy)] mt-2" data-testid="wizard-step-title">{STEPS[step]}</h1>

      <div className="mt-8" data-testid={`wizard-step-${step}`}>
        {step === 0 && (
          <div className="space-y-4">
            <input placeholder="Address line" value={form.address_line} onChange={e => upd("address_line", e.target.value)} className="w-full border border-slate-200 rounded-lg p-3 text-sm" data-testid="wiz-address"/>
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="City" value={form.city} onChange={e => upd("city", e.target.value)} className="border border-slate-200 rounded-lg p-3 text-sm" data-testid="wiz-city"/>
              <input placeholder="Postcode" value={form.postcode} onChange={e => upd("postcode", e.target.value)} className="border border-slate-200 rounded-lg p-3 text-sm" data-testid="wiz-postcode"/>
            </div>
            <select value={form.airport_code} onChange={e => upd("airport_code", e.target.value)} className="w-full border border-slate-200 rounded-lg p-3 text-sm" data-testid="wiz-airport">
              {airports.map(a => <option key={a.iata_code} value={a.iata_code}>{a.name} ({a.iata_code})</option>)}
            </select>
          </div>
        )}
        {step === 1 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {SPACE_TYPES.map(([v, l]) => (
              <button key={v} onClick={() => upd("space_type", v)} className={`border rounded-xl p-4 text-sm text-left ${form.space_type === v ? "border-[var(--fpn-navy)] bg-slate-50 font-semibold" : "border-slate-200"}`} data-testid={`wiz-space-${v}`}>{l}</button>
            ))}
          </div>
        )}
        {step === 2 && (
          <div>
            <div className="text-sm text-slate-600 mb-3">How many vehicles can be parked simultaneously?</div>
            <input type="number" min={1} max={50} value={form.capacity} onChange={e => upd("capacity", parseInt(e.target.value) || 1)} className="w-40 border border-slate-200 rounded-lg p-3 text-sm" data-testid="wiz-capacity"/>
          </div>
        )}
        {step === 3 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {VEHICLE_SIZES.map(v => (
              <label key={v} className={`border rounded-xl p-3 text-sm cursor-pointer ${form.vehicle_sizes.includes(v) ? "border-[var(--fpn-navy)] bg-slate-50 font-semibold" : "border-slate-200"}`} data-testid={`wiz-vsize-${v}`}>
                <input type="checkbox" checked={form.vehicle_sizes.includes(v)} onChange={() => toggle("vehicle_sizes", v)} className="mr-2"/>{v}
              </label>
            ))}
          </div>
        )}
        {step === 4 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {FEATURE_OPTIONS.map(f => (
              <label key={f} className={`border rounded-xl p-3 text-sm cursor-pointer ${form.features.includes(f) ? "border-[var(--fpn-navy)] bg-slate-50 font-semibold" : "border-slate-200"}`} data-testid={`wiz-feature-${f}`}>
                <input type="checkbox" checked={form.features.includes(f)} onChange={() => toggle("features", f)} className="mr-2"/>{f}
              </label>
            ))}
          </div>
        )}
        {step === 5 && (
          <div className="space-y-3">
            {KEY_OPTIONS.map(([v, l]) => (
              <label key={v} className={`block border rounded-xl p-4 text-sm cursor-pointer ${form.key_handling === v ? "border-[var(--fpn-navy)] bg-slate-50 font-semibold" : "border-slate-200"}`} data-testid={`wiz-keys-${v}`}>
                <input type="radio" checked={form.key_handling === v} onChange={() => upd("key_handling", v)} className="mr-2"/>{l}
              </label>
            ))}
          </div>
        )}
        {step === 6 && (
          <div>
            <PhotoUploader images={form.images} onChange={(imgs) => upd("images", imgs)} max={8} />
            {form.images.length > 0 && form.images.length < 3 && (
              <p className="text-xs text-amber-700 mt-3" data-testid="wiz-photos-warning">Please add at least {3 - form.images.length} more photo{3 - form.images.length === 1 ? "" : "s"} — a minimum of 3 is required.</p>
            )}
          </div>
        )}
        {step === 7 && (
          <div className="space-y-3">
            <input placeholder="Listing title" value={form.title} onChange={e => upd("title", e.target.value)} className="w-full border border-slate-200 rounded-lg p-3 text-sm" data-testid="wiz-title"/>
            <textarea placeholder="Description" value={form.description} onChange={e => upd("description", e.target.value)} rows={4} className="w-full border border-slate-200 rounded-lg p-3 text-sm" data-testid="wiz-description"/>
            <textarea placeholder="Parking instructions (customer will see after booking)" value={form.parking_instructions} onChange={e => upd("parking_instructions", e.target.value)} rows={3} className="w-full border border-slate-200 rounded-lg p-3 text-sm" data-testid="wiz-instructions"/>
          </div>
        )}
        {step === 8 && (
          <div className="grid grid-cols-2 gap-4">
            <label className="text-sm">Base price per day (£)
              <input type="number" step="0.50" value={form.base_daily_price} onChange={e => upd("base_daily_price", parseFloat(e.target.value) || 0)} className="mt-1 w-full border border-slate-200 rounded-lg p-3 text-sm" data-testid="wiz-price"/>
            </label>
            <label className="text-sm">Distance to airport (mi)
              <input type="number" step="0.1" value={form.distance_miles} onChange={e => upd("distance_miles", parseFloat(e.target.value) || 0)} className="mt-1 w-full border border-slate-200 rounded-lg p-3 text-sm" data-testid="wiz-distance"/>
            </label>
            <label className="text-sm">Transfer time (min)
              <input type="number" value={form.transfer_minutes} onChange={e => upd("transfer_minutes", parseInt(e.target.value) || 0)} className="mt-1 w-full border border-slate-200 rounded-lg p-3 text-sm" data-testid="wiz-transfer"/>
            </label>
          </div>
        )}
        {step === 9 && (
          <div className="space-y-4">
            <div className="border border-slate-200 rounded-2xl p-5 text-sm space-y-2">
              <Row label="Title" value={form.title || "—"}/>
              <Row label="Airport" value={form.airport_code}/>
              <Row label="Type" value={form.space_type}/>
              <Row label="Capacity" value={form.capacity}/>
              <Row label="Features" value={form.features.join(", ")}/>
              <Row label="Price / day" value={`£${form.base_daily_price}`}/>
            </div>
            <label className="flex items-start gap-2 text-sm" data-testid="wiz-terms">
              <input type="checkbox" checked={form.terms_accepted} onChange={e => upd("terms_accepted", e.target.checked)} className="mt-1"/>
              I confirm I own or have authority over this space, information is accurate, and I agree to the Host Terms.
            </label>
            {err && <div className="text-sm text-red-600" data-testid="wiz-error">{err}</div>}
          </div>
        )}
      </div>

      <div className="flex justify-between mt-10">
        <button onClick={prev} disabled={step === 0} className="text-sm text-slate-500 disabled:opacity-40" data-testid="wiz-back">← Back</button>
        {step < STEPS.length - 1 ? (
          <button onClick={next} className="btn-navy rounded-full px-6 py-3 text-sm" data-testid="wiz-next">Continue</button>
        ) : (
          <button onClick={submit} disabled={submitting} className="btn-amber rounded-full px-6 py-3 text-sm inline-flex items-center gap-2" data-testid="wiz-submit">
            <Check size={16}/>{submitting ? "Submitting…" : "Submit for review"}
          </button>
        )}
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between"><span className="text-slate-500">{label}</span><span className="font-medium">{String(value)}</span></div>
);
